import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { mdiCheckCircleOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { useToggle } from "usehooks-ts";
import * as Yup from "yup";

import Button from "components/Button";
import Input from "components/Input";
import Modal from "components/Modal";
import { routes } from "constants/routes";
import { RESPONSE_STATUS } from "constants/status";
import { showErrorMessage } from "helpers/toastMessage";
import AuthLayout from "layouts/Auth";
import { updateUserPasswordAPI } from "services/api/user";
import { AppDispatch } from "store";
import { handleLoading } from "store/global/loader";
import styles from "../ResetPassword/style.module.scss";

interface EnterNewPasswordForm {
  newPassword: string;
  confirmPassword: string;
}

const enterNewPasswordFormSchema = Yup.object({
  newPassword: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters"),
  confirmPassword: Yup.string()
    .required("Confirm Password is required")
    .oneOf([Yup.ref("newPassword")], "Passwords must match")
});

export default function EnterNewPassword() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSuccessModalOpen, toggleSuccessModal] = useToggle();
  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit
  } = useForm<EnterNewPasswordForm>({ resolver: yupResolver(enterNewPasswordFormSchema) });

  const token = searchParams.get("token") as string;
  const isSetMode = searchParams.get("mode") === "set";
  const title = isSetMode ? "Set your password" : "Reset Password";

  const goToSignIn = () => {
    navigate(routes.SIGNIN);
  };

  const onSubmit = async ({ confirmPassword }: EnterNewPasswordForm) => {
    dispatch(handleLoading(true));

    const {
      data: { status, message }
    } = await updateUserPasswordAPI(token ? token : "", confirmPassword).finally(() =>
      dispatch(handleLoading(false))
    );

    if (status === RESPONSE_STATUS.SUCCESS) {
      toggleSuccessModal();
    } else {
      showErrorMessage(message);
    }
  };

  // if no token is supplied, redirect to home page
  if (!token) {
    goToSignIn();
  }

  return (
    <AuthLayout>
      <form className={styles.resetPasswordContainer} onSubmit={handleSubmit(onSubmit)}>
        <h1 className="text-xl desktop:text-2xl">{title}</h1>
        <div className={styles.body}>
          <span>Enter a new password</span>
          <Input
            name="newPassword"
            label="&nbsp;&nbsp;New Password"
            register={register}
            error={errors.newPassword}
            isPasswordIcon
            required
          />
          {errors.newPassword && <p>{errors.newPassword.message}</p>}
          <Input
            name="confirmPassword"
            label="&nbsp;&nbsp;Confirm Password"
            register={register}
            error={errors.confirmPassword}
            isPasswordIcon
            required
          />
          {errors.confirmPassword && <p>{errors.confirmPassword.message}</p>}
        </div>
        <Button
          type="submit"
          size="lg"
          className="!px-8 desktop:!px-12 desktop:text-lg w-fit"
          loading={isSubmitting}>
          {title}
        </Button>

        <Modal
          isOpen={isSuccessModalOpen}
          closeModal={toggleSuccessModal}
          wrapperClass="!m-auto text-sm desktop:!min-h-fit desktop:!min-w-fit"
          dialogClass="!m-auto !bg-foreground !p-4 !border !border-card !overflow-y-auto gap-4"
          closeClass="!top-24 !right-6 !bg-transparent !text-placeholder">
          <div className="flex gap-1 text-xl font-bold text-success">
            <Icon path={mdiCheckCircleOutline} />
            <span>Success</span>
          </div>
          <p className="px-6">Your password has been successfully {isSetMode ? "set" : "reset"}.</p>
          <Button onClick={goToSignIn} className="self-end w-20 rounded-full h-9">
            OK
          </Button>
        </Modal>
      </form>
    </AuthLayout>
  );
}
